import React, { useEffect, useRef } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

interface Props {
  lat: number;
  lng: number;
  radiusKm: number;
  /** Called when the admin clicks the map to move the event center. */
  onPick: (lat: number, lng: number) => void;
}

/**
 * A small map showing an event's center and its geo-fence circle (the radius
 * used to auto-tag sightings). Click the map to move the center; the circle
 * tracks the radius. Uses OpenStreetMap tiles — no API key needed.
 */
const EventMap: React.FC<Props> = ({ lat, lng, radiusKm, onPick }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<L.Map | null>(null);
  const markerRef = useRef<L.CircleMarker | null>(null);
  const circleRef = useRef<L.Circle | null>(null);
  const onPickRef = useRef(onPick);
  onPickRef.current = onPick;

  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;
    const map = L.map(containerRef.current).setView([lat, lng], 9);
    L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '© OpenStreetMap contributors',
      maxZoom: 19,
    }).addTo(map);
    circleRef.current = L.circle([lat, lng], {
      radius: Math.max(radiusKm, 0) * 1000,
      color: '#0972d3',
      weight: 1,
      fillOpacity: 0.1,
    }).addTo(map);
    // A plain circleMarker avoids Leaflet's bundler-broken default marker icon.
    markerRef.current = L.circleMarker([lat, lng], {
      radius: 5,
      color: '#0972d3',
      fillColor: '#0972d3',
      fillOpacity: 1,
    }).addTo(map);
    map.on('click', (e: L.LeafletMouseEvent) => {
      onPickRef.current(Number(e.latlng.lat.toFixed(5)), Number(e.latlng.lng.toFixed(5)));
    });
    mapRef.current = map;
    // The map usually mounts inside a modal that sizes after this runs.
    setTimeout(() => map.invalidateSize(), 200);
    return () => {
      map.remove();
      mapRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keep the center + circle in sync with the form values.
  useEffect(() => {
    const center: L.LatLngExpression = [lat, lng];
    markerRef.current?.setLatLng(center);
    circleRef.current?.setLatLng(center);
    circleRef.current?.setRadius(Math.max(radiusKm, 0) * 1000);
    if (mapRef.current) mapRef.current.setView(center, mapRef.current.getZoom());
  }, [lat, lng, radiusKm]);

  return (
    <div
      ref={containerRef}
      style={{ height: 300, width: '100%', borderRadius: 8, overflow: 'hidden' }}
    />
  );
};

export default EventMap;
